/**
 * Die Blätter „Liefervertrag anlegen" und „Liefervertrag bearbeiten" (Spec 46c,
 * „Blatt") — Bauform `MeterCreateSheet` (Anlegen) und `MasterDataEditSheet`
 * (Bearbeiten, mit „Vertrag löschen" in zwei Schritten). Die Felder teilen sich
 * `SupplyFormFields`; Rümpfe baut `supply-form.ts`.
 *
 * **Der Anbieter ist ein Absender** (Stammdaten aus `features/documents`) und
 * kommt deshalb als fertiges Feld von außen (`providerField`), samt Auswahl
 * (`provider`) — der Container besitzt beides und auch das Blatt „Neuer
 * Anbieter". Geprüft wird am Server; ein 422 steht am Feld (`field`, der Code
 * kann `invalid` sein), alles andere (403/404/409) als Formularfehler.
 */

import { useRef, useState } from 'react'
import type { ReactNode, RefObject, SubmitEvent } from 'react'

import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  useCreateSupplyContract,
  useDeleteSupplyContract,
  useMeterOptions,
  useUpdateSupplyContract,
} from '@/features/energy/api'
import type { SupplyContract } from '@/features/energy/api'
import { toErrorState } from '@/features/energy/form-error'
import type { ErrorState } from '@/features/energy/form-error'
import { SupplyMoreFields, SupplyPrimaryFields } from '@/features/energy/SupplyFormFields'
import {
  canSaveSupply,
  deriveSupplyForm,
  emptySupplyForm,
  SUPPLY_FORM_FIELDS,
  SUPPLY_MORE_FIELDS,
  toSupplyCreate,
  toSupplyUpdate,
} from '@/features/energy/supply-form'
import type { SupplyFormValues } from '@/features/energy/supply-form'
import { ApiError } from '@/lib/api'

/** Der Anbieter als gewählter Absender. */
export interface SupplyProvider {
  readonly id: string
  readonly label: string
}

export interface SupplyMeterRef {
  readonly id: string
  readonly label: string
  /** Haus des Zählers, wenn sichtbar. */
  readonly houseLabel: string | null
}

function fieldSetter(setForm: (update: (current: SupplyFormValues) => SupplyFormValues) => void) {
  return (name: keyof SupplyFormValues) => (value: string) => {
    setForm((current) => ({ ...current, [name]: value }))
  }
}

const NO_UNITS: { value: string; label: string }[] = []

interface CreateProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly meter: SupplyMeterRef
  readonly provider: SupplyProvider | null
  readonly providerField: (error: FieldError | undefined) => ReactNode
  readonly onCreated: () => void
}

export function SupplyCreateSheet({ open, onOpenChange, meter, provider, providerField, onCreated }: CreateProps) {
  const createMutation = useCreateSupplyContract()
  const options = useMeterOptions(open)
  const contentRef = useRef<HTMLDivElement>(null)
  const [form, setForm] = useState<SupplyFormValues>(emptySupplyForm)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [errors, setErrors] = useState<ErrorState>({})
  const setField = fieldSetter(setForm)

  // Jedes Öffnen beginnt frisch (React-Muster „Adjusting state when a prop changes").
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(emptySupplyForm())
      setIsMoreOpen(false)
      setErrors({})
    }
  }

  const canSave = canSaveSupply(provider?.id, form) && !createMutation.isPending

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (provider === null || !canSave) return
    setErrors({})
    try {
      await createMutation.mutateAsync({ meterId: meter.id, body: toSupplyCreate(provider.id, form) })
      onOpenChange(false)
      onCreated()
    } catch (error) {
      const next = toErrorState(error, SUPPLY_FORM_FIELDS)
      if (next.field !== undefined && SUPPLY_MORE_FIELDS.has(next.field.field)) setIsMoreOpen(true)
      setErrors(next)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={contentRef}
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          // Beginnt die Auswahl noch ohne Anbieter, steht der Fokus im Suchfeld.
          event.preventDefault()
          contentRef.current?.querySelector<HTMLElement>('[data-testid="supply-create-provider-search"]')?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">Liefervertrag anlegen</SheetTitle>
            <p data-testid="supply-create-meter" className="text-label text-muted">
              {meter.houseLabel === null ? meter.label : `${meter.label} · ${meter.houseLabel}`}
            </p>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <SupplyPrimaryFields
              prefix="supply-create"
              form={form}
              onChange={setField}
              error={errors.field}
              providerField={providerField}
              noticeUnits={options.data?.notice_units ?? NO_UNITS}
              isOptionsPending={options.isPending}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="supply-create-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>
              <div className="flex flex-col gap-4 pt-2">
                <SupplyMoreFields prefix="supply-create" form={form} onChange={setField} error={errors.field} />
              </div>
            </details>
          </div>

          <SheetFooter>
            {errors.general !== undefined && (
              <p data-testid="supply-create-error" role="alert" className="text-label text-status-overdue">
                {errors.general}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="supply-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="supply-create-save"
                disabled={!canSave}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Liefervertrag anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

interface EditProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Der Vertrag, der bearbeitet wird; `null` solange das Blatt zu ist. */
  readonly contract: SupplyContract | null
  readonly provider: SupplyProvider | null
  readonly providerField: (error: FieldError | undefined) => ReactNode
  readonly triggerRef: RefObject<HTMLButtonElement | null>
  readonly onSaved: () => void
  readonly onDeleted: () => void
}

export function SupplyEditSheet({ open, onOpenChange, contract, provider, providerField, triggerRef, onSaved, onDeleted }: EditProps) {
  const updateMutation = useUpdateSupplyContract()
  const deleteMutation = useDeleteSupplyContract()
  const options = useMeterOptions(open)
  const [form, setForm] = useState<SupplyFormValues>(() => (contract === null ? emptySupplyForm() : deriveSupplyForm(contract)))
  const [errors, setErrors] = useState<ErrorState>({})
  const [isDeleteArmed, setIsDeleteArmed] = useState(false)
  const setField = fieldSetter(setForm)

  const [syncedKey, setSyncedKey] = useState(open ? contract?.id : undefined)
  const key = open ? contract?.id : undefined
  if (key !== syncedKey) {
    setSyncedKey(key)
    if (open && contract !== null) {
      setForm(deriveSupplyForm(contract))
      setErrors({})
      setIsDeleteArmed(false)
    }
  }

  if (contract === null) return null

  const providerId = provider?.id ?? contract.provider?.id
  const isDirty =
    JSON.stringify(form) !== JSON.stringify(deriveSupplyForm(contract)) ||
    (provider !== null && provider.id !== contract.provider?.id)
  const isBusy = updateMutation.isPending || deleteMutation.isPending

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (contract === null) return
    setErrors({})
    try {
      await updateMutation.mutateAsync({
        contractId: contract.id,
        body: toSupplyUpdate(contract, provider?.id, form),
        ifMatch: String(contract.version),
      })
      onOpenChange(false)
      onSaved()
    } catch (error) {
      setErrors(toErrorState(error, SUPPLY_FORM_FIELDS))
    }
  }

  async function handleDelete() {
    if (contract === null) return
    setErrors({})
    try {
      await deleteMutation.mutateAsync(contract.id)
      onOpenChange(false)
      onDeleted()
    } catch (error) {
      setIsDeleteArmed(false)
      setErrors({ general: error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.' })
    }
  }

  return (
    <MasterDataEditSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Liefervertrag bearbeiten"
      triggerRef={triggerRef}
      formId="supply-edit-form"
      saveTestId="supply-edit-save"
      saveDisabled={!isDirty || !canSaveSupply(providerId, form) || isBusy}
      isSaving={updateMutation.isPending}
    >
      <form
        id="supply-edit-form"
        onSubmit={(event) => void handleSave(event)}
        className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4"
      >
        <SupplyPrimaryFields
          prefix="supply-edit"
          form={form}
          onChange={setField}
          error={errors.field}
          providerField={providerField}
          noticeUnits={options.data?.notice_units ?? NO_UNITS}
          isOptionsPending={options.isPending}
        />
        <details open>
          <summary data-testid="supply-edit-more" className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2">
            Weitere Angaben
          </summary>
          <div className="flex flex-col gap-4 pt-2">
            <SupplyMoreFields prefix="supply-edit" form={form} onChange={setField} error={errors.field} />
          </div>
        </details>

        {errors.general !== undefined && (
          <p data-testid="supply-edit-error" role="alert" className="text-label text-status-overdue">
            {errors.general}
          </p>
        )}

        {/* Löschen fragt zurück: der erste Klick wird zu „Wirklich löschen". */}
        <div className="border-border flex items-center border-t pt-3">
          {isDeleteArmed ? (
            <button
              type="button"
              data-testid="supply-delete-confirm"
              disabled={isBusy}
              onClick={() => void handleDelete()}
              className="text-status-overdue text-body min-h-(--tap-min) px-1 font-semibold disabled:opacity-60"
            >
              Wirklich löschen
            </button>
          ) : (
            <button
              type="button"
              data-testid="supply-delete"
              disabled={isBusy}
              onClick={() => {
                setIsDeleteArmed(true)
              }}
              className="text-status-overdue text-body min-h-(--tap-min) px-1 disabled:opacity-60"
            >
              Vertrag löschen
            </button>
          )}
        </div>
      </form>
    </MasterDataEditSheet>
  )
}
